import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Button } from './ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip';
import { avatarTint, iniciales } from '../lib/identity';
import { estadoDe, ESTADOS_CERRADOS } from '../lib/estados';
import { cn } from '../lib/utils';
import { AlertTriangle, Check, Clock, DoorOpen, Stethoscope, Video } from 'lucide-react';

/**
 * Agenda del día como LISTA accionable (no como rejilla de calendario).
 *
 * · La fila de la cita que viene se marca "Siguiente": recepción sabe por dónde va
 *   sin leer toda la agenda.
 * · Las acciones (en sala / atendido) viven DENTRO de la fila y aparecen al pasar
 *   el cursor: la pantalla se ve limpia pero resolver una cita es un clic.
 * · El raíl de color es el doctor, así se distingue "quién" sin leer el nombre.
 */
export const AgendaDelDia = ({
  citas = [],
  pacientes = {},
  onSelect,
  onEstado,
  cargando = false,
  vacioTitulo = 'Hoy no hay citas programadas',
  vacioTexto = 'Cuando agenden la primera cita del día, aparecerá aquí.',
}) => {
  const ordenadas = [...citas].sort((a, b) => String(a.hora_inicio).localeCompare(String(b.hora_inicio)));

  const ahora = new Date();
  const ahoraMin = ahora.getHours() * 60 + ahora.getMinutes();
  const aMin = (hhmm) => {
    const [h, m] = String(hhmm || '').split(':').map(Number);
    return (h || 0) * 60 + (m || 0);
  };
  const siguiente = ordenadas.find((c) => !ESTADOS_CERRADOS.includes(c.estado) && aMin(c.hora_inicio) >= ahoraMin)
    || ordenadas.find((c) => !ESTADOS_CERRADOS.includes(c.estado));

  if (cargando) {
    return (
      <div className="space-y-2 p-4">
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className="flex items-center gap-3 rounded-lg border border-border/60 p-3">
            <div className="skeleton h-4 w-10" />
            <div className="skeleton h-9 w-9 rounded-full" />
            <div className="flex-1 space-y-2">
              <div className="skeleton h-3 w-40" />
              <div className="skeleton h-2.5 w-24" />
            </div>
            <div className="skeleton h-6 w-20 rounded-full" />
          </div>
        ))}
      </div>
    );
  }

  if (!ordenadas.length) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 px-6 py-14 text-center">
        <div className="grid h-12 w-12 place-items-center rounded-xl bg-muted">
          <Clock className="h-5 w-5 text-muted-foreground" />
        </div>
        <p className="text-sm font-semibold">{vacioTitulo}</p>
        <p className="max-w-sm text-[12.5px] text-muted-foreground">{vacioTexto}</p>
      </div>
    );
  }

  return (
    <ul className="stagger divide-y divide-border/60">
      {ordenadas.map((cita) => {
        const paciente = pacientes[cita.paciente_id];
        const nombre = cita.paciente_nombre || (paciente ? `${paciente.nombre} ${paciente.apellido}` : 'Paciente');
        const estado = estadoDe(cita.estado);
        const esSiguiente = siguiente?.id === cita.id;
        const cerrada = ESTADOS_CERRADOS.includes(cita.estado);
        const alertas = paciente?.alertas_medicas || [];

        return (
          <li key={cita.id}>
            <div
              className={cn(
                'group relative flex items-center gap-3 px-3.5 py-2.5 transition-colors',
                esSiguiente ? 'bg-primary/[0.045]' : 'hover:bg-muted/50',
                cerrada && 'opacity-70 hover:opacity-100',
              )}
            >
              {/* raíl = doctor */}
              <span
                className="absolute left-0 top-2 bottom-2 w-[3px] rounded-r-full"
                style={{ backgroundColor: cita.doctor_color || 'hsl(var(--primary))' }}
                aria-hidden="true"
              />

              <button
                type="button"
                onClick={() => onSelect?.(cita)}
                className="flex min-w-0 flex-1 items-center gap-3 text-left"
              >
                <span className="w-11 shrink-0 tnum text-[12.5px] font-semibold text-muted-foreground">
                  {cita.hora_inicio}
                </span>

                <Avatar className="h-9 w-9 shrink-0">
                  <AvatarImage src={paciente?.avatar_url} />
                  <AvatarFallback className="text-[11px] font-semibold text-white" style={avatarTint(nombre)}>
                    {iniciales(nombre)}
                  </AvatarFallback>
                </Avatar>

                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-1.5">
                    <span className="truncate text-[13.5px] font-semibold">{nombre}</span>
                    {alertas.length > 0 && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="shrink-0">
                            <AlertTriangle className="h-3.5 w-3.5 text-rose-500" />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          {alertas.map((a) => a.descripcion).join(' · ')}
                        </TooltipContent>
                      </Tooltip>
                    )}
                    {esSiguiente && (
                      <span className="shrink-0 rounded-full bg-primary px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-wide text-primary-foreground">
                        siguiente
                      </span>
                    )}
                  </span>
                  <span className="mt-0.5 flex items-center gap-1.5 text-[11.5px] text-muted-foreground">
                    <span className="truncate">{cita.motivo}</span>
                    {cita.modalidad === 'en_linea' && (
                      <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-info/10 px-1.5 py-0.5 text-[10px] font-semibold text-info">
                        <Video className="h-3 w-3" /> En línea
                      </span>
                    )}
                    {cita.hora_por_confirmar && (
                      <span className="shrink-0 rounded-full bg-warning/15 px-1.5 py-0.5 text-[10px] font-semibold text-warning">
                        Hora por confirmar
                      </span>
                    )}
                  </span>
                </span>

                <span className={cn(
                  'hidden shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset sm:inline-flex',
                  estado.pill,
                )}>
                  <span className={cn('h-1.5 w-1.5 rounded-full', estado.punto)} />
                  {estado.label}
                </span>
              </button>

              {/* Acciones in-context: aparecen al hover/focus de la fila */}
              {onEstado && !cerrada && (
                <div className="flex shrink-0 items-center gap-1 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
                  {cita.estado !== 'en_sala' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 gap-1 px-2 text-[11px] text-warning hover:bg-warning/10 hover:text-warning"
                      onClick={() => onEstado(cita, 'en_sala')}
                    >
                      <DoorOpen className="h-3.5 w-3.5" /> En sala
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 gap-1 px-2 text-[11px] text-success hover:bg-success/10 hover:text-success"
                    onClick={() => onEstado(cita, 'atendido')}
                  >
                    <Check className="h-3.5 w-3.5" /> Atendido
                  </Button>
                </div>
              )}

              {!onEstado && cita.doctor_nombre && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="hidden shrink-0 text-muted-foreground lg:block">
                      <Stethoscope className="h-3.5 w-3.5" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>{cita.doctor_nombre}</TooltipContent>
                </Tooltip>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
};

export default AgendaDelDia;
